import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import {Box, chakra} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getGDIHideStyles} from '../../utils/gdi-styles'
import {renderLayoutsSwitch, renderComponentsSwitch} from '../../../utils/contentRenderer'

const SIMPLE_TABLE_CELL_WIDTH = '97.7px'
const TABLE_BORDER_COLOR = 'webTypo.textDisabled'

// The Denim "Size Chart" table (Size & Fit sub-tab) is the only SimpleTable
// on the site shaped as: a row-title column ('Size') + exactly 4 data columns
// (Number Size, Denim Size, Natural Waist, Low Hips). For this specific shape
// only, on desktop the row-title column is pinned to 67px and the remaining
// columns split the rest of the width equally so the table fills its
// container; on mobile the row-title column keeps its normal (measured)
// width and the data columns get a fixed 106px each, scrolling horizontally
// like every other SimpleTable (Tops/Bottoms/Swimwear/etc.).
const SIZE_CHART_ROW_TITLE_WIDTH = '67px'
const SIZE_CHART_MOBILE_DATA_COLUMN_WIDTH = '106px'
const SIZE_CHART_DATA_COLUMN_COUNT = 4

/**
 * Renders a CMS ContentArea as JSX, falling back to null.
 * Used for CustomTitleContent / CustomRowContent rich-content cells.
 */
const renderCustomContent = (contentArea) => {
    if (!Array.isArray(contentArea) || contentArea.length === 0) return null
    return contentArea.map((item, i) => {
        const exp = item?.ContentLink?.Expanded
        if (!exp) return null
        return (
            <React.Fragment key={i}>
                {renderLayoutsSwitch(exp) || renderComponentsSwitch(exp)}
            </React.Fragment>
        )
    })
}

const SIMPLE_TABLE_STICKY_SEPARATOR =
    'inset -1px 0 0 0 var(--chakra-colors-webTypo-textDisabled)'

const SIMPLE_TABLE_CELL_SELECTOR = 'thead th, tbody th, tbody td'
const SIMPLE_TABLE_SCROLL_INDICATOR_WIDTH = 40

const parsePixelValue = (value) => Number.parseFloat(value || '0') || 0

const resolveUniformCellWidth = (cells, minimumWidth = SIMPLE_TABLE_CELL_WIDTH) => {
    const minimum = Number.parseFloat(minimumWidth)
    const measuredWidths = Array.from(cells || [], (cell) =>
        Math.max(
            cell?.scrollWidth || 0,
            cell?.clientWidth || 0,
            cell?.getBoundingClientRect?.().width || 0
        )
    )
    const maxMeasuredWidth = measuredWidths.length > 0 ? Math.max(...measuredWidths) : 0
    return `${Math.max(minimum, Math.ceil(maxMeasuredWidth))}px`
}

const getHorizontalScrollState = (element) => {
    if (!element) return {hasHorizontalOverflow: false, canScrollLeft: false, canScrollRight: false}

    const maxScrollLeft = Math.max(element.scrollWidth - element.clientWidth, 0)
    const scrollLeft = Math.max(element.scrollLeft, 0)

    return {
        hasHorizontalOverflow: maxScrollLeft > 0,
        canScrollLeft: scrollLeft > 0,
        canScrollRight: scrollLeft < maxScrollLeft - 1
    }
}

const getHorizontalScrollIndicatorState = (element) => {
    if (!element) return {thumbWidth: '100%', thumbLeft: '0%'}

    const clientWidth = Math.max(element.clientWidth || 0, 0)
    const scrollWidth = Math.max(element.scrollWidth || 0, 0)
    const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0)
    const thumbWidth = `${SIMPLE_TABLE_SCROLL_INDICATOR_WIDTH}px`

    if (clientWidth === 0 || maxScrollLeft === 0) {
        return {thumbWidth, thumbLeft: '0px'}
    }

    const travelWidth = Math.max(clientWidth - SIMPLE_TABLE_SCROLL_INDICATOR_WIDTH, 0)
    const thumbLeft = (Math.max(element.scrollLeft, 0) / maxScrollLeft) * travelWidth

    return {
        thumbWidth,
        thumbLeft: `${thumbLeft}px`
    }
}

/**
 * Convert an inch string to a cm string.
 * Handles the ASCII straight quote (U+0022), the Unicode right double
 * quotation mark (U+201D) and the double prime / inch symbol (U+2033).
 * Handles single values ('23"') and ranges ('31-32"', '37.5-39"').
 * e.g. '31-32"' → '78.7-81.3 cm', '23"' → '58.4 cm'
 */
const convertToMetric = (value) => {
    if (typeof value !== 'string') return value
    const match = value.match(/^([\d.]+)(?:-([\d.]+))?["\u201D\u2033]?$/)
    if (!match) return value
    const lo = (parseFloat(match[1]) * 2.54).toFixed(1)
    if (!match[2]) return `${lo} cm`
    const hi = (parseFloat(match[2]) * 2.54).toFixed(1)
    return `${lo}-${hi} cm`
}

/**
 * GDITableLayoutBlock
 *
 * CMS shape:
 *   TableStyle                  — variant token ("SimpleTable" | "ContentTable")
 *   TableTitle                  — text for the top-left corner cell (1A); often empty
 *   ColumnsTitles               — array of {SimpleTextTitleContent, CustomTitleContent}
 *   GDITableLayoutBlockRows     — array of {GDITableLayoutBlockRowTitle, ConvertibleRow,
 *                                           RowContent: [{SimpleTextRowContent, CustomRowContent}]}
 *   HideBlockOn                 — standard GDI visibility field
 */
const GDITableLayoutBlock = ({TableStyle, TableTitle, ColumnsTitles, GDITableLayoutBlockRows: Rows, HideBlockOn}) => {
    const [unit, setUnit] = useState('IN')
    const [uniformCellWidth, setUniformCellWidth] = useState(null)
    const [scrollState, setScrollState] = useState({
        hasHorizontalOverflow: false,
        canScrollLeft: false,
        canScrollRight: false
    })
    const [scrollIndicatorState, setScrollIndicatorState] = useState({
        thumbWidth: '100%',
        thumbLeft: '0%'
    })
    const scrollContainerRef = useRef(null)
    const scrollIndicatorTrackRef = useRef(null)
    const dragStateRef = useRef({isDragging: false, pointerOffset: 0})

    const hideStyles = getGDIHideStyles(HideBlockOn)
    const columns = ColumnsTitles ?? []
    const numCols = columns.length
    const rows = Rows ?? []

    // Graph nulls these local blocks when the page query hits MaxExpansionDepth
    // (repaired server-side in utils/optimizely-depth-repair.js). Decided here
    // but applied after the hooks below, so content arriving later can never
    // change the hook count between renders.
    const shouldRender =
        !hideStyles.doNotLoadComponent && numCols > 0 && columns.some(Boolean)

    const hasRowTitles = rows.some((r) => r?.GDITableLayoutBlockRowTitle)
    const isSizeGuide = TableStyle === 'SimpleTable'
    const isContentTable = TableStyle === 'ContentTable'
    const hasConverter = isSizeGuide && rows.some((r) => r?.ConvertibleRow)
    const isSizeChartTable =
        isSizeGuide && hasRowTitles && numCols === SIZE_CHART_DATA_COLUMN_COUNT

    // Row-title cells (the 'Size' column) get a fixed 67px width on both
    // breakpoints (unchanged); the data columns split the rest of the row
    // equally on desktop but use a fixed 106px on mobile (with horizontal
    // scroll, same as every other SimpleTable).
    const getSizeGuideWidthProps = (isRowTitleCell) => {
        if (!isSizeGuide) return {}
        if (isSizeChartTable) {
            return isRowTitleCell
                ? {
                      width: SIZE_CHART_ROW_TITLE_WIDTH,
                      minWidth: SIZE_CHART_ROW_TITLE_WIDTH,
                      maxWidth: SIZE_CHART_ROW_TITLE_WIDTH
                  }
                : {
                      width: {
                          base: SIZE_CHART_MOBILE_DATA_COLUMN_WIDTH,
                          lg: `calc((100% - ${SIZE_CHART_ROW_TITLE_WIDTH}) / ${numCols})`
                      },
                      minWidth: {base: SIZE_CHART_MOBILE_DATA_COLUMN_WIDTH, lg: 0}
                  }
        }
        return {
            minWidth: SIMPLE_TABLE_CELL_WIDTH,
            ...(uniformCellWidth && {
                width: uniformCellWidth,
                maxWidth: uniformCellWidth
            })
        }
    }

    const thProps = {
        textStyle: isSizeGuide ? 'bodyXsBold' : 'bodyMdRegular',
        textAlign: 'left',
        px: isSizeGuide ? '12px' : '4px',
        py: isSizeGuide ? '10px' : '14px',
        ...(isSizeGuide
            ? {
                  border: '1px solid',
                  borderColor: TABLE_BORDER_COLOR,
                  backgroundColor: 'webTheme.backgroundSecondaryBrand',
                  whiteSpace: isSizeChartTable ? 'normal' : 'nowrap'
              }
            : {
                  border: 'none',
                  borderColor: TABLE_BORDER_COLOR
              })
    }

    const tdProps = {
        textStyle: isSizeGuide ? 'bodyXsRegular' : 'bodyMdRegular',
        textAlign: isSizeGuide ? 'center' : 'left',
        verticalAlign: 'top',
        px: isSizeGuide ? '12px' : '4px',
        py: isSizeGuide ? '10px' : '14px',
        ...(isSizeGuide
            ? {
                  border: '1px solid',
                  borderColor: TABLE_BORDER_COLOR,
                  whiteSpace: isSizeChartTable ? 'normal' : 'nowrap'
              }
            : {
                  border: 'none',
                  borderColor: TABLE_BORDER_COLOR
              })
    }

    const getCellContent = (cell, isConvertible) => {
        if (!cell) return null
        const raw = cell.SimpleTextRowContent
        const convertCell = isConvertible && !cell.DontConvertThisCell
        if (raw) return unit === 'CM' && convertCell ? convertToMetric(raw) : raw
        return renderCustomContent(cell.CustomRowContent)
    }

    const syncScrollState = () => {
        const nextState = getHorizontalScrollState(scrollContainerRef.current)
        setScrollState((currentState) =>
            currentState.hasHorizontalOverflow === nextState.hasHorizontalOverflow &&
            currentState.canScrollLeft === nextState.canScrollLeft &&
            currentState.canScrollRight === nextState.canScrollRight
                ? currentState
                : nextState
        )
    }

    const syncScrollIndicatorState = () => {
        const nextState = getHorizontalScrollIndicatorState(scrollContainerRef.current)
        setScrollIndicatorState((currentState) =>
            currentState.thumbWidth === nextState.thumbWidth &&
            currentState.thumbLeft === nextState.thumbLeft
                ? currentState
                : nextState
        )
    }

    const syncUniformCellWidth = () => {
        if (!isSizeGuide || isSizeChartTable) return

        const nextWidth = resolveUniformCellWidth(
            scrollContainerRef.current?.querySelectorAll(SIMPLE_TABLE_CELL_SELECTOR)
        )

        setUniformCellWidth((currentWidth) =>
            currentWidth === nextWidth ? currentWidth : nextWidth
        )
    }

    const syncScrollUi = () => {
        syncScrollState()
        syncScrollIndicatorState()
    }

    const setScrollFromThumbLeft = (nextThumbLeft) => {
        const scrollContainer = scrollContainerRef.current
        const trackWidth = scrollContainer?.clientWidth || 0
        const maxScrollLeft = Math.max((scrollContainer?.scrollWidth || 0) - trackWidth, 0)
        const maxThumbLeft = Math.max(trackWidth - SIMPLE_TABLE_SCROLL_INDICATOR_WIDTH, 0)

        if (!scrollContainer || maxScrollLeft <= 0 || maxThumbLeft <= 0) return

        const clampedThumbLeft = Math.min(Math.max(nextThumbLeft, 0), maxThumbLeft)
        scrollContainer.scrollLeft = (clampedThumbLeft / maxThumbLeft) * maxScrollLeft
        syncScrollUi()
    }

    const handleScrollIndicatorThumbMouseDown = (event) => {
        const trackLeft =
            scrollIndicatorTrackRef.current?.getBoundingClientRect?.().left || 0
        const thumbLeft = parsePixelValue(scrollIndicatorState.thumbLeft)
        dragStateRef.current = {
            isDragging: true,
            pointerOffset: event.clientX - trackLeft - thumbLeft
        }
    }

    const handleScrollIndicatorTrackMouseDown = (event) => {
        if (event.target === event.currentTarget) {
            const trackLeft =
                scrollIndicatorTrackRef.current?.getBoundingClientRect?.().left || 0
            setScrollFromThumbLeft(
                event.clientX - trackLeft - SIMPLE_TABLE_SCROLL_INDICATOR_WIDTH / 2
            )
        }
    }

    useEffect(() => {
        if (!shouldRender) return undefined

        const handleMouseMove = (event) => {
            if (!dragStateRef.current.isDragging) return

            const trackLeft =
                scrollIndicatorTrackRef.current?.getBoundingClientRect?.().left || 0
            setScrollFromThumbLeft(event.clientX - trackLeft - dragStateRef.current.pointerOffset)
        }

        const handleMouseUp = () => {
            dragStateRef.current = {isDragging: false, pointerOffset: 0}
        }

        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', handleMouseUp)

        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [scrollIndicatorState.thumbLeft, shouldRender])

    useLayoutEffect(() => {
        if (!shouldRender || !isSizeGuide) return undefined

        syncScrollUi()
        syncUniformCellWidth()

        const handleResize = () => {
            syncScrollUi()
            syncUniformCellWidth()
        }

        window.addEventListener('resize', handleResize)

        return () => window.removeEventListener('resize', handleResize)
    }, [shouldRender, isSizeGuide, columns, rows, unit, TableTitle])

    if (!shouldRender) return null

    return (
        <Box
            data-block-type="GDITableLayoutBlock"
            data-table-style={TableStyle}
            sx={hideStyles.sx}
            width={isSizeChartTable ? '100%' : {base: '100%', lg: 'auto'}}
            minWidth="0"
        >
            {hasConverter && (
                <Box display="flex" gap="4px" mb={4}>
                    {['IN', 'CM'].map((u) => (
                        <chakra.button
                            key={u}
                            type="button"
                            onClick={() => setUnit(u)}
                            textStyle="labelXs"
                            px={2}
                            py={1}
                            border="none"
                            backgroundColor={
                                unit === u
                                    ? 'webTheme.backgroundReverse'
                                    : 'webTheme.backgroundPrimaryBrand'
                            }
                            color={unit === u ? 'webTypo.textOnColor' : undefined}
                            cursor="pointer"
                        >
                            {u === 'IN' ? (
                                <FormattedMessage
                                    id="gdi_table_layout.unit.inches"
                                    defaultMessage="IN"
                                />
                            ) : (
                                <FormattedMessage
                                    id="gdi_table_layout.unit.centimeters"
                                    defaultMessage="CM"
                                />
                            )}
                        </chakra.button>
                    ))}
                </Box>
            )}
        <Box
            data-scroll-container="true"
            ref={scrollContainerRef}
            overflowX="auto"
            onScroll={syncScrollUi}
            scrollbarWidth={isSizeGuide ? 'none' : undefined}
            msOverflowStyle={isSizeGuide ? 'none' : undefined}
            sx={
                isSizeGuide
                    ? {
                          '::-webkit-scrollbar': {
                              display: 'none'
                          }
                      }
                    : undefined
            }
            {...(isSizeGuide && {
                borderLeftWidth: '1px',
                borderLeftStyle: 'solid',
                borderLeftColor: TABLE_BORDER_COLOR,
                borderRightWidth: scrollState.canScrollRight ? '1px' : '0',
                borderRightStyle: 'solid',
                borderRightColor: TABLE_BORDER_COLOR
            })}
        >
            <chakra.table
                minWidth={isSizeChartTable ? '0' : 'max-content'}
                width={isSizeChartTable ? '100%' : undefined}
                sx={{
                    'borderCollapse': 'collapse',
                    'borderSpacing': 0,
                    'tableLayout': isSizeChartTable ? 'fixed' : 'auto'
                }}
            >
                <chakra.thead>
                    <chakra.tr>
                        {hasRowTitles && (
                            <chakra.th
                                {...thProps}
                                {...getSizeGuideWidthProps(true)}
                                display={isSizeGuide ? undefined : {base: 'none', md: 'table-cell'}}
                                position="sticky"
                                left={0}
                                zIndex={1}
                                borderLeft="none"
                                borderRight="none"
                                sx={{
                                    ...(isSizeGuide && scrollState.canScrollLeft && {
                                        boxShadow: SIMPLE_TABLE_STICKY_SEPARATOR
                                    })
                                }}
                                {...(!isSizeGuide && { backgroundColor: 'white' })}
                            >
                                {TableTitle || null}
                            </chakra.th>
                        )}
                        {columns.map((col, i) => (
                            <chakra.th
                                key={i}
                                {...thProps}
                                {...getSizeGuideWidthProps(false)}
                                textAlign={isSizeGuide ? 'center' : thProps.textAlign}
                            >
                                {col?.SimpleTextTitleContent ||
                                    renderCustomContent(col?.CustomTitleContent)}
                            </chakra.th>
                        ))}
                    </chakra.tr>
                </chakra.thead>
                <chakra.tbody>
                    {(() => {
                        // Pre-compute desktop alternating background per row.
                        // Only rows that actually render a rowTitle are counted;
                        // rows without a title are never highlighted.
                        let titleRowCounter = 0
                        const rowBgs = rows.map((row) => {
                            const title = row?.GDITableLayoutBlockRowTitle
                            if (isContentTable && hasRowTitles && title) {
                                return titleRowCounter++ % 2 === 0
                                    ? {base: 'transparent', md: 'webTheme.backgroundPrimaryBrand'}
                                    : undefined
                            }
                            return undefined
                        })
                        return rows.map((row, rowIdx) => {
                            const isConvertible = !!row?.ConvertibleRow
                            const rowTitle = row?.GDITableLayoutBlockRowTitle
                            const rowCells = row?.RowContent ?? []
                            const hasMobileTitle = isContentTable && hasRowTitles && !!rowTitle
                            const dataRowBg = rowBgs[rowIdx]
                            return (
                                <React.Fragment key={rowIdx}>
                                    {hasMobileTitle && (
                                        <chakra.tr display={{base: 'table-row', md: 'none'}}>
                                            <chakra.td colSpan={numCols} padding={0} border="none">
                                                <Box
                                                    display="inline-block"
                                                    position="sticky"
                                                    left={0}
                                                    width="100vw"
                                                    textStyle={thProps.textStyle}
                                                    px={thProps.px}
                                                    py={thProps.py}
                                                    backgroundColor="webTheme.backgroundPrimaryBrand"
                                                >
                                                    {rowTitle}
                                                </Box>
                                            </chakra.td>
                                        </chakra.tr>
                                    )}
                                    <chakra.tr backgroundColor={dataRowBg}>
                                        {hasRowTitles && (
                                            <chakra.th
                                                {...thProps}
                                                {...getSizeGuideWidthProps(true)}
                                                display={isSizeGuide ? undefined : {base: 'none', md: 'table-cell'}}
                                                position="sticky"
                                                left={0}
                                                zIndex={1}
                                                borderLeft="none"
                                                borderRight="none"
                                                sx={{
                                                    ...(isSizeGuide && scrollState.canScrollLeft && {
                                                        boxShadow: SIMPLE_TABLE_STICKY_SEPARATOR
                                                    })
                                                }}
                                                {...(isSizeGuide
                                                    ? { backgroundColor: 'webTheme.backgroundNotification' }
                                                    : { backgroundColor: dataRowBg ? 'webTheme.backgroundPrimaryBrand' : 'white' }
                                                )}
                                                fontWeight={isSizeGuide ? 400 : undefined}
                                            >
                                                {rowTitle || null}
                                            </chakra.th>
                                        )}
                                        {Array.from({length: numCols}, (_, colIdx) => (
                                            <chakra.td
                                                key={colIdx}
                                                {...tdProps}
                                                {...getSizeGuideWidthProps(false)}
                                                {...(hasMobileTitle && {
                                                    borderTop: {base: '1px solid', md: 'none'},
                                                    borderColor: TABLE_BORDER_COLOR
                                                })}
                                            >
                                                {getCellContent(rowCells[colIdx], isConvertible)}
                                            </chakra.td>
                                        ))}
                                    </chakra.tr>
                                </React.Fragment>
                            )
                        })
                    })()}
                </chakra.tbody>
            </chakra.table>
        </Box>
            {scrollState.hasHorizontalOverflow && (
                <Box
                    data-scroll-indicator="true"
                    ref={scrollIndicatorTrackRef}
                    position="relative"
                    mt="9px"
                    height="9px"
                    onMouseDown={handleScrollIndicatorTrackMouseDown}
                >
                    <Box
                        data-scroll-indicator-thumb="true"
                        position="absolute"
                        top={0}
                        left={scrollIndicatorState.thumbLeft}
                        width={scrollIndicatorState.thumbWidth}
                        height="9px"
                        borderRadius="50px"
                        backgroundColor="webTypo.textDisabled"
                        cursor="grab"
                        onMouseDown={handleScrollIndicatorThumbMouseDown}
                    />
                </Box>
            )}
        </Box>
    )
}

GDITableLayoutBlock.displayName = 'GDITableLayoutBlock'

GDITableLayoutBlock.propTypes = {
    TableStyle: PropTypes.string,
    TableTitle: PropTypes.string,
    ColumnsTitles: PropTypes.arrayOf(PropTypes.object),
    GDITableLayoutBlockRows: PropTypes.arrayOf(PropTypes.object),
    HideBlockOn: PropTypes.string,
    GDITableLayoutBlockAudienceTargeting: PropTypes.object
}

export default GDITableLayoutBlock

export {resolveUniformCellWidth}
